import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

export const AdminPaymentRecords = () => {
  const [records, setRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, paymentsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/payment-records`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
      ]);
      setRecords(recordsRes.data);
      setPayments(paymentsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    }
  };

  const getPaymentTitle = (paymentId) => {
    const payment = payments.find((p) => p.id === paymentId);
    return payment?.title || 'Bilinmiyor';
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Bilinmiyor';
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/payment-records/${recordId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
    };
    const labels = {
      pending: 'Bekliyor',
      paid: 'Ödendi',
      overdue: 'Gecikmiş',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="payment-records-title">
              Ödeme Kayıtları
            </h1>
            <p className="text-base text-[#595959] mt-2">Kim ödedi, kim ödemedi takibi</p>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl hidden md:block" data-testid="payment-records-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kullanıcı</TableHead>
                  <TableHead>Ödeme</TableHead>
                  <TableHead>Tutar</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Ödeme Tarihi</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id} data-testid={`record-row-${record.id}`}>
                    <TableCell>{getUserName(record.user_id)}</TableCell>
                    <TableCell>{getPaymentTitle(record.payment_id)}</TableCell>
                    <TableCell>{record.amount} ₺</TableCell>
                    <TableCell>{getStatusBadge(record.status)}</TableCell>
                    <TableCell>
                      {record.paid_date ? new Date(record.paid_date).toLocaleDateString('tr-TR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={record.status}
                        onValueChange={(value) => handleStatusChange(record.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`status-select-${record.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Bekliyor</SelectItem>
                          <SelectItem value="paid">Ödendi</SelectItem>
                          <SelectItem value="overdue">Gecikmiş</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="payment-records-mobile">
            {records.map((record) => (
              <div key={record.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid={`record-card-${record.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] truncate">{getUserName(record.user_id)}</p>
                    <p className="text-xs text-[#595959] truncate">{getPaymentTitle(record.payment_id)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold text-[#2563EB]">{record.amount} ₺</p>
                    <div className="mt-1">{getStatusBadge(record.status)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#E5E5E5]">
                  <span className="text-xs text-[#595959]">
                    {record.paid_date ? `Ödeme: ${new Date(record.paid_date).toLocaleDateString('tr-TR')}` : 'Henüz ödenmedi'}
                  </span>
                  <Select
                    value={record.status}
                    onValueChange={(value) => handleStatusChange(record.id, value)}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Bekliyor</SelectItem>
                      <SelectItem value="paid">Ödendi</SelectItem>
                      <SelectItem value="overdue">Gecikmiş</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      
    </PageLayout>
  );
};
